import React, { useContext, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../App';

/**
 * RequireAuth
 * -----------
 * Shared auth-gate wrapper. Fixes the systemic auth-race redirect bug where
 * pages redirected authenticated users to /auth before the auth check resolved.
 *
 * States:
 *   - authResolved === false  -> auth still loading, render a lightweight
 *                                placeholder (NO navigate — do not punish a
 *                                legitimate authenticated session for being slow).
 *   - authResolved && !user   -> definitively unauthenticated, navigate to /auth
 *                                and preserve the attempted path so Auth.js can
 *                                return the user here after login.
 *   - authResolved && user    -> authenticated, render children.
 */
export default function RequireAuth({ children }) {
  const { user, authResolved } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (authResolved && !user) {
      // Preserve the attempted path so Auth.js can return the user here after login.
      // Uses the existing sessionStorage key the Auth page already honors.
      try {
        sessionStorage.setItem(
          'lk_post_auth_redirect',
          location.pathname + location.search
        );
      } catch {
        /* storage unavailable — proceed without redirect-back */
      }
      navigate('/auth', {
        replace: true,
        state: { from: location.pathname + location.search },
      });
    }
  }, [authResolved, user, navigate, location.pathname, location.search]);

  // Auth still resolving — render a minimal neutral placeholder.
  // Keeps the shell on-screen so URL stays stable and the user does not get
  // flashed back to a public page while cookies are being validated.
  if (!authResolved) {
    return (
      <div
        data-testid="require-auth-loading"
        style={{
          minHeight: '60vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--fy-text-secondary, #6b7280)',
          fontSize: '0.9rem',
        }}
      >
        <span>Loading…</span>
      </div>
    );
  }

  // Auth resolved and no user — effect above will redirect. Render nothing in the meantime.
  if (!user) {
    return null;
  }

  return children;
}
